/**
 * Phase 9.3 — Compliance anomaly banner.
 *
 * Fetches /api/compliance/anomalies and renders each active anomaly as a
 * red card at the top of /admin-compliance. Supports per-anomaly dismiss
 * (hits POST /{id}/dismiss) and also honors a local-only "hide for this
 * session" map in sessionStorage.
 */
import React, { useEffect, useState } from 'react'
import { AlertTriangle, X, ChevronRight } from 'lucide-react'

const API_URL = process.env.REACT_APP_BACKEND_URL || ''
const SESSION_KEY = 'lwp_dismissed_anomalies'

function readDismissed() {
  try {
    return new Set(JSON.parse(sessionStorage.getItem(SESSION_KEY) || '[]'))
  } catch (_) {
    return new Set()
  }
}

function writeDismissed(ids) {
  try {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(Array.from(ids)))
  } catch (_) {
    /* ignore */
  }
}

export default function AnomalyBanner({ adminKey }) {
  const [items, setItems] = useState([])
  const [hiddenLocal, setHiddenLocal] = useState(readDismissed)
  const [expanded, setExpanded] = useState(true)

  useEffect(() => {
    if (!adminKey) return
    fetch(`${API_URL}/api/compliance/anomalies?admin_key=${encodeURIComponent(adminKey)}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((d) => setItems(d.anomalies || []))
      .catch(() => setItems([]))
  }, [adminKey])

  const hideLocal = (id) => {
    const next = new Set(hiddenLocal)
    next.add(id)
    setHiddenLocal(next)
    writeDismissed(next)
  }

  const dismissServer = async (id) => {
    hideLocal(id)
    try {
      await fetch(
        `${API_URL}/api/compliance/anomalies/${encodeURIComponent(id)}/dismiss?admin_key=${encodeURIComponent(adminKey)}`,
        { method: 'POST' }
      )
    } catch (_) {
      /* keep local dismiss even if server call fails */
    }
  }

  const visible = items.filter((a) => !hiddenLocal.has(a.anomaly_id))
  if (visible.length === 0) return null

  return (
    <div
      data-testid="anomaly-banner"
      style={{
        background: '#FEE2E2',
        border: '1px solid #DC2626',
        color: '#7F1D1D',
        borderRadius: 10,
        marginBottom: 16,
        overflow: 'hidden',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', padding: '12px 16px', gap: 12 }}>
        <AlertTriangle size={20} style={{ flexShrink: 0, color: '#DC2626' }} />
        <div style={{ flex: 1, fontSize: 13, fontWeight: 600 }}>
          {visible.length} compliance anomal{visible.length === 1 ? 'y' : 'ies'} detected — review before taking action.
        </div>
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          data-testid="anomaly-toggle"
          style={{
            background: 'transparent',
            border: 'none',
            color: '#7F1D1D',
            cursor: 'pointer',
            fontSize: 12,
            fontWeight: 500,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
          }}
        >
          {expanded ? 'Hide' : 'Show'}
          <ChevronRight size={14} style={{ transform: expanded ? 'rotate(90deg)' : 'rotate(0)', transition: 'transform 150ms' }} />
        </button>
      </div>
      {expanded && (
        <div style={{ borderTop: '1px solid #DC262633', padding: '10px 16px', background: '#FEF2F2' }}>
          {visible.map((a) => (
            <div
              key={a.anomaly_id}
              data-testid={`anomaly-${a.anomaly_id}`}
              style={{ padding: '8px 0', borderBottom: '1px dashed #FCA5A5', fontSize: 12, lineHeight: 1.5 }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                <div style={{ fontWeight: 600, textTransform: 'capitalize' }}>
                  {a.metric.replace(/_/g, ' ')} · obs {a.observed} · z = {a.z_score.toFixed(2)}
                </div>
                <div style={{ display: 'inline-flex', gap: 6 }}>
                  <button
                    type="button"
                    onClick={() => hideLocal(a.anomaly_id)}
                    title="Hide for this session only"
                    data-testid={`anomaly-hide-${a.anomaly_id}`}
                    style={{ background: 'transparent', border: 'none', color: '#7F1D1D', cursor: 'pointer', fontSize: 11 }}
                  >
                    Hide
                  </button>
                  <button
                    type="button"
                    onClick={() => dismissServer(a.anomaly_id)}
                    title="Dismiss permanently"
                    data-testid={`anomaly-dismiss-${a.anomaly_id}`}
                    style={{ background: 'transparent', border: 'none', color: '#7F1D1D', cursor: 'pointer' }}
                  >
                    <X size={14} />
                  </button>
                </div>
              </div>
              <div style={{ color: '#991B1B', marginTop: 2 }}>{a.explanation}</div>
              <div style={{ color: '#9CA3AF', fontSize: 10, fontFamily: 'monospace', marginTop: 2 }}>
                baseline μ={a.baseline_mean.toFixed(2)} σ={a.baseline_stddev.toFixed(2)} · id={a.anomaly_id}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
